<script lang="ts" setup>
import BigNumber from 'bignumber.js';
import { useRoute } from 'vue-router';

import SitePasswordModal from '@/components/modals/SitePasswordModal.vue';
import Notifications from '@/components/notifications/Notifications.vue';
import ThirdPartyServicesModal from '@/components/web3/ThirdPartyServicesModal.vue';
import useWeb3Watchers from '@/composables/watchers/useWeb3Watchers';
import { DEFAULT_TOKEN_DECIMALS } from '@/constants/tokens';
import { getWeb3Provider } from '@/dependencies/wallets/Web3Provider';
import useWeb3 from '@/services/web3/useWeb3';
import { walletService } from '@/services/web3/wallet.service';
import { useAppKitAccount } from '@reown/appkit/vue';

import { useI18n } from 'vue-i18n';
import GlobalModalContainer from './components/modals/GlobalModalContainer.vue';
import AppSidebar from './components/navs/AppNav/AppSidebar/AppSidebar.vue';
import SanctionedWalletModal from './components/web3/SanctionedWalletModal.vue';
import useAlerts, {
  Alert,
  AlertPriority,
  AlertType,
} from './composables/useAlerts';
import useBackgroundColor from './composables/useBackgroundColor';
import useGnosisSafeApp from './composables/useGnosisSafeApp';
import useNavigationGuards from './composables/useNavigationGuards';
import useNetwork from './composables/useNetwork';
import { useSidebar } from './composables/useSidebar';
import { useThirdPartyServices } from './composables/useThirdPartyServices';
import useExploitWatcher from './composables/watchers/useExploitWatcher';
import useGlobalQueryWatchers from './composables/watchers/useGlobalQueryWatchers';
import usePoolCreationWatcher from './composables/watchers/usePoolCreationWatcher';
import { provideGlobalPools } from './providers/local/global-pools.provider';

// Dynamic import of layout components:
// it prevents the initial bundle from including all the layouts (and their unique dependencies)
// each route will only load the layout that it needs
const DefaultLayout = defineAsyncComponent(
  () => import('@/pages/_layouts/DefaultLayout.vue')
);
const BlankLayout = defineAsyncComponent(
  () => import('@/pages/_layouts/BlankLayout.vue')
);
const FocussedLayout = defineAsyncComponent(
  () => import('@/pages/_layouts/FocussedLayout.vue')
);
const ContentLayout = defineAsyncComponent(
  () => import('@/pages/_layouts/ContentLayout.vue')
);
const PoolLayout = defineAsyncComponent(
  () => import('@/pages/_layouts/PoolLayout.vue')
);

BigNumber.config({ DECIMAL_PLACES: DEFAULT_TOKEN_DECIMALS });

/**
 * APP PROVIDERS
 */
provideGlobalPools();

/**
 * STATE
 */
const layout = ref('DefaultLayout');

const Layouts = {
  BlankLayout: BlankLayout,
  ContentLayout: ContentLayout,
  DefaultLayout: DefaultLayout,
  FocussedLayout: FocussedLayout,
  PoolLayout: PoolLayout,
};
/**
 * COMPOSABLES
 */
useWeb3Watchers();
usePoolCreationWatcher();
useGlobalQueryWatchers();
useGnosisSafeApp();
useExploitWatcher();
useNavigationGuards();
const { isBlocked, provider } = useWeb3();
const route = useRoute();
const { newRouteHandler: updateBgColorFor } = useBackgroundColor();
const { sidebarOpen } = useSidebar();
const { addAlert } = useAlerts();
const { handleThirdPartyModalToggle, isThirdPartyServicesModalVisible } =
  useThirdPartyServices();
const { t } = useI18n();
const router = useRouter();
const { networkSlug } = useNetwork();
const accountData = useAppKitAccount();

const userProvider = computed(() => {
  const Web3Provider = getWeb3Provider();
  return new Web3Provider(provider.value as any, 'any');
});

// OPTIONAL FEATURE ALERTS are enabled by featureAlertEnabled toggle
const featureAlert: Alert = {
  id: 'csp-alert',
  priority: AlertPriority.HIGH,
  label: t('poolWarnings.cspPoolVulnWarning.generalTitle'), // Add the new feature alert text here and set featureAlertEnabled to true to activate it
  type: AlertType.ERROR,
  rememberClose: false,
  actionLabel: 'Recovery exit',
  action: () => {
    router.push({ name: 'recovery-exit', params: { networkSlug } });
  },
};
const featureAlertEnabled = false;
if (featureAlertEnabled) addAlert(featureAlert);

/**
 * WATCHERS
 */
watch(route, newRoute => {
  updateBgColorFor(newRoute);
  if (newRoute.meta.layout) {
    layout.value = newRoute.meta.layout as string;
  } else {
    layout.value = 'DefaultLayout';
  }
});

watch(
  () => [accountData.value.address, accountData.value.isConnected],
  async ([_address, _isConnected]) => {
    if (_address && _isConnected) {
      // Add small delay to ensure provider is fully initialized
      await new Promise(resolve => setTimeout(resolve, 500));
      if (userProvider.value) {
        walletService.setUserProvider(userProvider);
      }
    }
  }
);
</script>

<template>
  <div id="modal" />
  <div id="app">
    <component :is="Layouts[layout]" />

    <SitePasswordModal />
    <SanctionedWalletModal :isVisible="isBlocked" />
    <ThirdPartyServicesModal
      :isVisible="isThirdPartyServicesModalVisible"
      @close="handleThirdPartyModalToggle(false)"
    />
    <AppSidebar v-if="sidebarOpen" />
    <Notifications />
  </div>
  <GlobalModalContainer />
</template>
